import { useState } from "react";
import ActionButton from "../atoms/ActionButton";
import ConfirmModal from "../molecules/ConfirmModal";
import RegiFormRow from "../molecules/RegiFormRow";

// 新規レジ行の初期値ファクトリ
const newRegiRow = () => ({
  _key: `new_${Date.now()}_${Math.random()}`,
  regiNo: null,
  regiName: "",
  userName: "",
  userID: "",
  password: "",
  confirmPassword: "",
});

/**
 * ShopManagementForm - 店舗・レジの追加・編集フォーム
 *
 * mode='add': 空フォームで新規店舗作成。レジを1件以上必須。
 * mode='edit': 既存値をプリセットして編集。パスワード欄は常に空欄から開始。
 *
 * @param {string}   mode              - 'add' | 'edit'
 * @param {object}   initialData       - 編集時の初期データ（shop オブジェクト）
 * @param {function} onSubmit          - (payload) => Promise 保存ハンドラ
 * @param {function} onCancel          - キャンセルハンドラ
 * @param {function} onDeactivateShop  - (shopNo) => Promise 店舗無効化ハンドラ
 * @param {function} onDeactivateRegi  - (shopNo, regiNo) => Promise レジ無効化ハンドラ
 */
function ShopManagementForm({
  mode,
  initialData,
  onSubmit,
  onCancel,
  onDeactivateShop,
  onDeactivateRegi,
}) {
  const isEdit = mode === "edit";

  const [shopName, setShopName] = useState(initialData?.ShopName ?? "");
  const [buildingName, setBuildingName] = useState(
    initialData?.BuildingName ?? "",
  );

  // 編集時はアクティブなレジのみ表示。パスワード欄は常に空欄で開始
  const [registers, setRegisters] = useState(() => {
    if (!isEdit || !initialData?.registers) return [newRegiRow()];
    const activeRegis = initialData.registers.filter((r) => r.IsActive === 1);
    if (activeRegis.length === 0) return [newRegiRow()];
    return activeRegis.map((r) => ({
      _key: String(r.RegiNo),
      regiNo: r.RegiNo,
      regiName: r.RegiName ?? "",
      userName: r.UserName ?? "",
      userID: r.UserID ?? "",
      password: "",
      confirmPassword: "",
    }));
  });

  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [showEditModal, setShowEditModal] = useState(false);

  const updateRegister = (idx, field, value) => {
    setRegisters((prev) =>
      prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)),
    );
    // エラーをクリア
    if (errors[`regi_${idx}_${field}`]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[`regi_${idx}_${field}`];
        return next;
      });
    }
  };

  const addRegister = () => {
    setRegisters((prev) => [...prev, newRegiRow()]);
  };

  const removeNewRegister = (idx) => {
    setRegisters((prev) => prev.filter((_, i) => i !== idx));
  };

  const validate = () => {
    const errs = {};
    if (!shopName.trim()) errs.shopName = "店舗名は必須です";

    registers.forEach((r, idx) => {
      if (!r.regiName.trim())
        errs[`regi_${idx}_regiName`] = "レジ部門名は必須です";
      if (!r.userID.trim())
        errs[`regi_${idx}_userID`] = "ログインIDは必須です";

      if (r.regiNo === null) {
        // 新規レジ: パスワード必須
        if (!r.password) {
          errs[`regi_${idx}_password`] = "パスワードは必須です（新規追加時）";
        } else if (!/^[\x20-\x7E]+$/.test(r.password)) {
          errs[`regi_${idx}_password`] =
            "パスワードは半角英数字・記号のみ使用できます";
        } else if (r.password !== r.confirmPassword) {
          errs[`regi_${idx}_confirmPassword`] = "パスワードが一致しません";
        }
      } else if (r.password) {
        // 既存レジのパスワード変更
        if (!/^[\x20-\x7E]+$/.test(r.password)) {
          errs[`regi_${idx}_password`] =
            "パスワードは半角英数字・記号のみ使用できます";
        } else if (r.password !== r.confirmPassword) {
          errs[`regi_${idx}_confirmPassword`] = "パスワードが一致しません";
        }
      }
    });

    return errs;
  };

  // 「保存する」ボタン押下: バリデーション → 編集時は確認モーダル → doSubmit
  const handleSubmitClick = () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    if (isEdit) {
      setShowEditModal(true);
    } else {
      doSubmit();
    }
  };

  // 実際の送信処理
  const doSubmit = async () => {
    setErrors({});
    setShowEditModal(false);
    setSubmitting(true);
    try {
      await onSubmit({
        shopName: shopName.trim(),
        buildingName: buildingName.trim(),
        registers: registers.map((r) => ({
          regiNo: r.regiNo,
          regiName: r.regiName.trim(),
          userName: r.userName.trim(),
          userID: r.userID.trim(),
          password: r.password || null,
        })),
      });
    } catch (err) {
      // UserID重複エラー: 該当フィールドにエラーを表示する
      if (err.conflictUserIds && err.conflictUserIds.length > 0) {
        const newErrors = {};
        registers.forEach((r, idx) => {
          if (err.conflictUserIds.includes(r.userID.trim())) {
            newErrors[`regi_${idx}_userID`] = "このログインIDは既に使用されています";
          }
        });
        setErrors(newErrors);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivateShop = async () => {
    if (
      !window.confirm(
        `「${shopName}」を無効化します。\nこの店舗の全レジもまとめてログイン不可になります。\n\n再有効化が必要な場合はDB直接操作での対応となります。よろしいですか？`,
      )
    )
      return;
    setSubmitting(true);
    try {
      await onDeactivateShop(initialData.ShopNo);
    } catch {
      // エラーは親が処理する
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivateRegi = async (regi) => {
    if (
      !window.confirm(
        `レジ「${regi.regiName}」（No.${regi.regiNo}）を無効化します。\nこのレジはログイン不可になります。\n\n再有効化が必要な場合はDB直接操作での対応となります。よろしいですか？`,
      )
    )
      return;
    setSubmitting(true);
    try {
      await onDeactivateRegi(initialData.ShopNo, regi.regiNo);
      // 無効化したレジをフォームから除去する
      setRegisters((prev) => prev.filter((r) => r.regiNo !== regi.regiNo));
    } catch {
      // エラーは親が処理する
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <h2 className="text-lg font-bold mb-4">
        {isEdit ? "店舗情報を編集" : "新規店舗を追加"}
      </h2>

      {/* 店舗情報セクション */}
      <div className="border border-gray-300 rounded p-4 mb-4">
        <h3 className="text-lg font-bold text-gray-600 mb-3">店舗情報</h3>

        {isEdit && (
          <div className="flex items-center gap-3 mb-3">
            <span className="text-base text-gray-500 w-24 shrink-0">店舗番号</span>
            <span className="text-base text-gray-700">{initialData.ShopNo}</span>
          </div>
        )}

        <div className="flex items-start gap-3 mb-3">
          <label className="text-base text-gray-600 w-24 shrink-0 pt-2">
            店舗名 *
          </label>
          <div className="flex-1">
            <input
              type="text"
              value={shopName}
              onChange={(e) => {
                setShopName(e.target.value);
                if (errors.shopName)
                  setErrors((p) => { const n = { ...p }; delete n.shopName; return n; });
              }}
              className="border border-gray-400 rounded px-2 py-1 text-base w-full"
              disabled={submitting}
            />
            {errors.shopName && (
              <p className="text-red-600 text-base mt-1">{errors.shopName}</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <label className="text-base text-gray-600 w-24 shrink-0">建物名</label>
          <input
            type="text"
            value={buildingName}
            onChange={(e) => setBuildingName(e.target.value)}
            className="border border-gray-400 rounded px-2 py-1 text-base flex-1"
            disabled={submitting}
          />
        </div>
      </div>

      {/* レジ情報セクション */}
      <div className="border border-gray-300 rounded p-4 mb-4">
        <h3 className="text-lg font-bold text-gray-600 mb-3">レジ情報</h3>

        {registers.map((regi, idx) => (
          <RegiFormRow
            key={regi._key}
            regi={regi}
            idx={idx}
            isEdit={isEdit}
            canRemove={regi.regiNo === null && registers.length > 1}
            errors={errors}
            submitting={submitting}
            onChange={updateRegister}
            onRemove={() => removeNewRegister(idx)}
            onDeactivate={() => handleDeactivateRegi(regi)}
          />
        ))}

        <button
          type="button"
          onClick={addRegister}
          disabled={submitting}
          className="w-full mt-2 border border-dashed border-gray-400 rounded px-4 py-2 text-base text-gray-500 hover:text-gray-700 hover:border-gray-500 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          ＋ レジを追加
        </button>
      </div>

      {/* 操作ボタン */}
      <div className="flex gap-3 mb-6">
        <ActionButton
          label="キャンセル"
          onClick={onCancel}
          disabled={submitting}
          paddingClass="px-5 py-2"
          textSizeClass="text-base"
        />
        <ActionButton
          label={isEdit ? "保存する" : "追加する"}
          onClick={handleSubmitClick}
          disabled={submitting}
          paddingClass="px-5 py-2"
          textSizeClass="text-base"
          colorClass="bg-gray-700 text-white"
        />
      </div>

      {/* 編集確認モーダル */}
      {showEditModal && (
        <ConfirmModal
          message={
            "店舗・レジ情報を変更しようとしています。\n既存情報を別の店舗・レジとして流用すると、過去の売上データと混在し正しく集計できなくなります。\n新しい店舗を追加する場合は「キャンセル」→「新規追加」から登録してください。\n内容を確定してよろしいですか？"
          }
          onConfirm={doSubmit}
          onCancel={() => setShowEditModal(false)}
        />
      )}

      {/* 店舗無効化（編集時のみ） */}
      {isEdit && (
        <div className="border-t border-gray-200 pt-4">
          <p className="text-sm text-gray-400 mb-2">危険な操作</p>
          <ActionButton
            label="この店舗を無効化する"
            onClick={handleDeactivateShop}
            disabled={submitting}
            paddingClass="px-4 py-2"
            textSizeClass="text-base"
            colorClass="bg-white text-red-500"
          />
          <p className="text-sm text-gray-400 mt-1">
            無効化すると、この店舗のすべてのレジがログイン不可になります。
          </p>
        </div>
      )}
    </div>
  );
}

export default ShopManagementForm;
